import { Constants } from "appConstants";
import {
	IcoAttachment,
	IcoCloseError,
	IcoFeedbackSmallCheck,
	IcoPlus,
	IcoRefresh,
	IcoTrash
} from "assets/icons";
import Label from "components/Label";
import { useCallback } from "react";
import { FileInputProps, InnerStateProps, StyledNodeProps } from "./types";

function ControlsRow({ children }: Readonly<StyledNodeProps>) {
	return (
		<div className="flex flex-row gap-4 w-full justify-center items-center">
			{children}
		</div>
	);
}

function ControlsRowLabels({ children }: Readonly<StyledNodeProps>) {
	return (
		<div className="flex flex-col w-full justify-center items-start text-sm">
			{children}
		</div>
	);
}

function StatusIndicatorRow({
	children
}: Readonly<StyledNodeProps>): JSX.Element {
	return (
		<div className="w-full flex flex-col gap-1 justify-start">{children}</div>
	);
}

function InputLabel({
	children,
	isRequired
}: Readonly<StyledNodeProps> & { isRequired: boolean }): JSX.Element {
	return (
		<Label isRequired={!!isRequired} hideOptional>
			{children}
		</Label>
	);
}

export function UploadErrorState(
	props: Readonly<FileInputProps & InnerStateProps>
): JSX.Element {
	const {
		fileName,
		required = true,
		uploadError,
		uploadFile,
		error,
		touched
	} = props;
	const UPLOAD_ERROR_MESSAGE =
		Constants.strings?.errors?.files?.uploadErrorMessage;

	const validationErrorMessage = Array.isArray(error)
		? error.join("\n")
		: String(error);
	const errorMessage = uploadError
		? UPLOAD_ERROR_MESSAGE
		: validationErrorMessage;
	const showErrorMessage = (error && touched) || uploadError;

	const onClick = useCallback(
		(evt: any) => {
			evt.stopPropagation();
			uploadFile();
		},
		[uploadFile]
	);

	return (
		<>
			<ControlsRow>
				<IcoCloseError />
				<ControlsRowLabels>
					<InputLabel isRequired={!!required}>
						{Constants.strings.errors.files.errorToAttach}
					</InputLabel>
					{fileName && <span className="text-xs">{fileName}</span>}
				</ControlsRowLabels>
				<button
					type="button"
					aria-label={Constants.strings.errors.files.tryAgain}
					onClick={onClick}
					onKeyDown={onClick}
				>
					<IcoRefresh />
				</button>
			</ControlsRow>
			{showErrorMessage && (
				<StatusIndicatorRow>
					<div className="w-full h-1 rounded bg-[#F83446]" />
					<div className="white-space--pre-line mt-1 text-xs">
						{errorMessage}
					</div>
				</StatusIndicatorRow>
			)}
		</>
	);
}

export function RegularState(
	props: Readonly<FileInputProps & InnerStateProps>
): JSX.Element {
	const {
		value,
		isUploading,
		uploadProgress,
		required = false,
		label,
		details
	} = props;
	return (
		<>
			<ControlsRow>
				<IcoAttachment size="24" />
				<ControlsRowLabels>
					<InputLabel isRequired={!!required}>{label}</InputLabel>
					<span className="text-xs">{value}</span>
				</ControlsRowLabels>
				<IcoPlus size="24" color="#1D1D1D" />
			</ControlsRow>
			<hr className="w-full border-[#C6C7C8]" />
			{details && <span className="text-sm">{details}</span>}
			{isUploading && (
				<StatusIndicatorRow>
					<div className="w-full h-1 relative">
						<div className="absolute w-full h-1 rounded bg-[#F0F0F0]" />
						<div
							className="absolute h-1 rounded bg-[#1D1D1D]"
							style={{ width: `${uploadProgress}%` }}
						/>
					</div>
					<div className="white-space--pre-line mt-1 text-xs">
						{Constants.strings.inputs.files.attachmentProgressMessage(
							uploadProgress
						)}
					</div>
				</StatusIndicatorRow>
			)}
		</>
	);
}

export function SuccessState(
	props: Readonly<FileInputProps & InnerStateProps>
): JSX.Element {
	const { onAttachmentChange, fileName, required = true } = props;

	const onClick = useCallback(
		(evt: any) => {
			evt.stopPropagation();
			onAttachmentChange("");
		},
		[fileName, onAttachmentChange]
	);

	return (
		<>
			<ControlsRow>
				<div>
					<IcoFeedbackSmallCheck />
				</div>
				<ControlsRowLabels>
					<InputLabel isRequired={!!required}>
						{Constants.strings.inputs.files.documentAttached}
					</InputLabel>
					{fileName && <span className="text-xs">{fileName}</span>}
				</ControlsRowLabels>
				<button
					type="button"
					aria-label="limpar campo"
					onClick={onClick}
					onKeyDown={onClick}
				>
					<IcoTrash />
				</button>
			</ControlsRow>
			<StatusIndicatorRow>
				<div className="w-full h-1 rounded bg-[#00D4A1]" />
				<div className="white-space--pre-line mt-1 text-xs">
					{Constants.strings.inputs.files.successfulAttachment}
				</div>
			</StatusIndicatorRow>
		</>
	);
}
